import React, { useState, useEffect, useCallback } from "react";
import "./../../../assets/css/purchase/form.css";
import moment from "moment";

// Components
import Breadcrumbs from "../../components/Breadcrumbs";
import Sidebar from "../../components/Sidebar";
import ReportsTable from "../../components/Content/ReportsTable";
import DatePicker from "../../components/DatePicker";

// Utils
import { getAlert, getDataWithJoin } from "../../../utils/SamuiUtils";
import { getReport } from "../../../utils/ReportUtils";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const InternalMaterialRequisitionReport = () => {
  const [name, setName] = useState(null);
  const [searchCriteria, setSearchCriteria] = useState({
    wtDocDate: moment("2025-07-23").format("YYYY-MM-DD"),
  });
  const [filteredData, setFilteredData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(16);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const [exportController, setExportController] = useState({
    exportToExcel: null,
    isExporting: false,
    hasData: false,
  });
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    setName("10. รายงานเบิกวัสดุภายใน");
    setSearchCriteria({
      wtDocDate: moment("2025-07-23").format("YYYY-MM-DD"),
    });
    setFilteredData([]);
    setSummaryData([]);
    setCurrentPage(1);
    setItemsPerPage(16);
  };

  const handleChangeDate = (value, name) => {
    setSearchCriteria((prev) => ({
      ...prev,
      [name]: value ? value.format("YYYY-MM-DD") : null,
    }));
  };

  // ฟังก์ชันคำนวณ summary จาก main data
  const calculateSummaryFromMainData = (data) => {
    const summary = {};

    data.forEach((item) => {
      const dept = item.Dept_Name || "ไม่ระบุแผนก";
      const total = parseFloat(item.Item_Total) || 0;

      if (summary[dept]) {
        summary[dept] += total;
      } else {
        summary[dept] = total;
      }
    });

    // แปลงเป็น array และเรียงตาม dept name
    const summaryArray = Object.keys(summary)
      .sort()
      .map((dept) => ({
        Dept_Name: dept,
        Total_Amount: summary[dept],
      }));

    setSummaryData(summaryArray);
  };

  const handleSearch = async () => {
    try {
      setFilteredData([]);
      setSummaryData([]);

      let compId = "";
      try {
        compId = localStorage.getItem("company");
      } catch (error) {
        console.error("Error parsing company data:", error);
        getAlert("ERROR", "ไม่สามารถอ่านข้อมูลบริษัทได้");
        return;
      }

      if (!compId) {
        getAlert("ERROR", "ไม่พบข้อมูลบริษัท");
        return;
      }

      // สร้าง WHERE condition โดยแยก ORDER BY ออกมา
      let whereCondition = "";
      if (searchCriteria.wtDocDate) {
        whereCondition = `WHERE WT.Comp_Id IN (2) AND WT.WTDoc_Status = 1 AND CONVERT(DATE, WT.WTDoc_Date) = CONVERT(DATE, '${searchCriteria.wtDocDate}') ORDER BY WT.WTDoc_No ASC`;
      } else {
        whereCondition = `WHERE WT.Comp_Id IN (2) AND WT.WTDoc_Status = 1 ORDER BY WT.WTDoc_No ASC`;
      }

      const selectFields = `WT.WTDoc_No, WT.Item_Code, WT.Item_Name,
               WT.Item_Desc,
               WT.F_WT_Code + ' ' + WT.F_WT_Name AS Warehouse,
               WT.Group_Name, WT.Item_Qty, WT.Item_Price_Unit,
               WT.Item_Unit, WT.Item_Total, WT.Emp_Name,
               WT.Dept_Name, WT.WTDoc_Remark,
               WT.Comp_Id AS WTH_Comp_Id, WT.Comp_Id AS WTD_Comp_Id, WT.Comp_Id AS ITM_Comp_Id, WT.Comp_Id AS EMP_Comp_Id`;

      const fromTable = `view_Samui_PU_ICE WT`;
      const joinTables = ``;

      const response = await getDataWithJoin(
        selectFields,
        fromTable,
        joinTables,
        whereCondition
      );

      if (response && response.length > 0) {
        setFilteredData(response);
        // คำนวณ summary จาก main data
        calculateSummaryFromMainData(response);
        console.debug("Data set successfully, length:", response.length);
      } else {
        setFilteredData([]);
        setSummaryData([]);
        console.log("No data received from API");
      }
    } catch (error) {
      console.error("Search Error:", error);
      getAlert("ERROR", "เกิดข้อผิดพลาดในการค้นหาข้อมูล: " + error.message);
      setFilteredData([]);
      setSummaryData([]);
    }
  };

  const handleExportReady = useCallback((state) => {
    setExportController((prev) => {
      if (
        prev.exportToExcel === state.exportToExcel &&
        prev.isExporting === state.isExporting &&
        prev.hasData === state.hasData
      ) {
        return prev;
      }
      return state;
    });
  }, []);

  const handleExportClick = useCallback(async () => {
    if (!Array.isArray(filteredData) || filteredData.length === 0) {
      getAlert("FAILED", "ไม่มีข้อมูลสำหรับ Export");
      return;
    }

    try {
      setIsExportingExcel(true);

      const workbook = new ExcelJS.Workbook();
      const sheetName = "รายงานเบิกวัสดุภายใน";
      const worksheet = workbook.addWorksheet(sheetName);

      const reportDate = searchCriteria.wtDocDate
        ? moment(searchCriteria.wtDocDate).format("DD/MM/YYYY")
        : "-";

      const headerLines = [
        "10. รายงานเบิกวัสดุภายใน",
        `วันที่เบิกวัสดุ: ${reportDate}`,
      ];

      const columnCount = 12;
      headerLines.forEach((line, index) => {
        const headerRow = worksheet.addRow([line]);
        worksheet.mergeCells(headerRow.number, 1, headerRow.number, columnCount);
        headerRow.height = 20;
        headerRow.getCell(1).font = {
          bold: true,
          size: index === 0 ? 16 : 12,
        };
        headerRow.getCell(1).alignment = {
          horizontal: "center",
          vertical: "middle",
        };
      });

      worksheet.addRow([]);

      const tableHeaders = [
        "เลขที่ใบเบิก",
        "รหัสวัสดุ",
        "รายการวัสดุ",
        "รายละเอียดสินค้า",
        "คลังที่",
        "กลุ่มวัสดุ",
        "จำนวน",
        "ราคาหน่วย",
        "หน่วย",
        "จำนวนเงิน",
        "ผู้เบิก",
        "แผนก",
        "หมายเหตุ",
      ];

      worksheet.addRow(tableHeaders);

      worksheet.getRow(headerLines.length + 2).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFEF6C00" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });

      filteredData.forEach((item) => {
        worksheet.addRow([
          item.WTDoc_No || "-",
          item.Item_Code || "-",
          item.Item_Name || "-",
          item.Item_Desc || "-",
          item.Warehouse || "-",
          item.Group_Name || "-",
          item.Item_Qty || 0,
          item.Item_Price_Unit || 0,
          item.Item_Unit || "-",
          item.Item_Total || 0,
          item.Emp_Name || "-",
          item.Dept_Name || "-",
          item.WTDoc_Remark || "-",
        ]);
      });

      worksheet.columns = [
        { key: "WTDoc_No", width: 18 },
        { key: "Item_Code", width: 18 },
        { key: "Item_Name", width: 25 },
        { key: "Item_Desc", width: 25 },
        { key: "Warehouse", width: 18 },
        { key: "Group_Name", width: 18 },
        { key: "Item_Qty", width: 12 },
        { key: "Item_Price_Unit", width: 12 },
        { key: "Item_Unit", width: 10 },
        { key: "Item_Total", width: 15 },
        { key: "Emp_Name", width: 18 },
        { key: "Dept_Name", width: 18 },
        { key: "WTDoc_Remark", width: 20 },
      ];

      const summarySheet = workbook.addWorksheet("สรุปยอดรวมตามแผนก");

      summarySheet.addRow(["สรุปยอดรวมตามแผนก"]);
      summarySheet.mergeCells(1, 1, 1, 2);
      summarySheet.getCell("A1").font = { bold: true, size: 16 };
      summarySheet.getCell("A1").alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      summarySheet.addRow([]);

      summarySheet.addRow(["แผนก", "ยอดรวม (บาท)"]);
      summarySheet.getRow(3).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFEF6C00" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });

      if (summaryData.length > 0) {
        summaryData.forEach((item) => {
          summarySheet.addRow([
            item.Dept_Name || "-",
            Number(item.Total_Amount || 0),
          ]);
        });

        const total = summaryData.reduce(
          (sum, item) => sum + (Number(item.Total_Amount) || 0),
          0
        );
        const totalRow = summarySheet.addRow(["รวมทั้งหมด", total]);
        totalRow.eachCell((cell) => {
          cell.font = { bold: true };
          cell.alignment = { vertical: "middle", horizontal: "center" };
        });
      } else {
        summarySheet.addRow(["ไม่พบข้อมูล", ""]);
      }

      summarySheet.columns = [
        { key: "Dept_Name", width: 30 },
        { key: "Total_Amount", width: 20 },
      ];

      summarySheet.getColumn("Total_Amount").numFmt = "#,##0.00";

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer]),
        `รายงานเบิกวัสดุภายใน_${moment().format("YYYYMMDD_HHmmss")}.xlsx`
      );
    } catch (error) {
      console.error("Export internal material requisition report error:", error);
      getAlert("FAILED", "ไม่สามารถ Export ข้อมูลได้");
    } finally {
      setIsExportingExcel(false);
    }
  }, [filteredData, summaryData, searchCriteria.wtDocDate]);

  const handlePrintReport = useCallback(() => {
    const rawDate = searchCriteria?.wtDocDate;

    if (!rawDate) {
      getAlert("FAILED", "กรุณาเลือกวันที่เบิกวัสดุก่อนพิมพ์รายงาน");
      return;
    }

    const docMoment = moment(rawDate, "YYYY-MM-DD", true);
    if (!docMoment.isValid()) {
      getAlert("FAILED", "รูปแบบวันที่เบิกวัสดุไม่ถูกต้อง");
      return;
    }

    const docDate = `${docMoment.format("YYYY-MM-DD")} 00:00:00.000`;

    // ถ้าเป็นโรงน้ำ ให้เรียกโรงน้ำ ถ้าเป็นโรงน้ำแข็ง ให้เรียกโรงน้ำแข็ง
    if (localStorage.getItem("company") === "1") {
      getReport("pu_water", docDate);
    } else {
      getReport("pu_ice", docDate);
    }

  }, [searchCriteria?.wtDocDate]);

  return (
    <>
      <Sidebar />
      <div className="main-panel">
        <div className="container">
          <div className="page-inner">
            <div className="page-header d-flex justify-content-between align-items-center">
              <Breadcrumbs
                page={name}
                items={[
                  { name: "รายงาน", url: "/reports" },
                  { name: name, url: "/internal-material-requisition-report" },
                ]}
              />
            </div>
            <div className="row">
              <div className="col-md-12">
                <div className="card">
                  <div className="card-header">
                    <h4>เงื่อนไขการค้นหา</h4>
                  </div>
                  <div className="card-body">
                    <div className="row g-3 align-items-end">
                      <div className="col-md-2">
                        <label htmlFor="wtDocDate" className="form-label">
                          วันที่เบิกวัสดุ
                        </label>
                        <DatePicker
                          label=""
                          name="wtDocDate"
                          value={searchCriteria.wtDocDate}
                          onChange={(date) =>
                            handleChangeDate(date, "wtDocDate")
                          }
                          disabled={false}
                        />
                      </div>
                      <div className="col-md-12 d-flex justify-content-end">
                        <button
                          type="button"
                          className="btn btn-lg shadow text-white me-2"
                          style={{
                            backgroundColor: "#EF6C00",
                            fontSize: "16px",
                            minWidth: "150px",
                          }}
                          onClick={() => handleSearch()}
                        >
                          <i className="fas fa-search" /> ค้นหา
                        </button>
                        <button
                          type="button"
                          className="btn btn-lg shadow text-white me-2"
                          style={{
                            backgroundColor: "#EF6C00",
                            fontSize: "16px",
                            minWidth: "150px",
                          }}
                          onClick={handlePrintReport}
                        >
                          <i className="fas fa-print" /> พิมพ์รายงาน
                        </button>
                        <button
                          type="button"
                          className="btn btn-lg shadow text-white me-2"
                          style={{
                            backgroundColor: "#EF6C00",
                            fontSize: "16px",
                            minWidth: "150px",
                          }}
                          onClick={handleExportClick}
                          disabled={
                            (!exportController.hasData ||
                              exportController.isExporting ||
                              isExportingExcel)
                          }
                        >
                          <i className="fas fa-file-excel" />{" "}
                          {exportController.isExporting || isExportingExcel
                            ? "กำลังส่งออก..."
                            : "Export"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-lg shadow text-white"
                          style={{
                            backgroundColor: "#EF6C00",
                            fontSize: "16px",
                            minWidth: "150px",
                          }}
                          onClick={() => initialize()}
                        >
                          <i className="fas fa-undo" /> เริ่มใหม่
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <ReportsTable
                currentItems={currentItems}
                currentPage={currentPage}
                handlePageChange={(pageNumber) => setCurrentPage(pageNumber)}
                dataMasterList={filteredData}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                setCurrentPage={setCurrentPage}
                fieldMappings={{
                  docNo: "WTDoc_No",
                  itemCode: "Item_Code",
                  itemName: "Item_Name",
                  itemDesc: "Item_Desc",
                  warehouse: "Warehouse",
                  groupName: "Group_Name",
                  itemQty: "Item_Qty",
                  itemPriceUnit: "Item_Price_Unit",
                  itemUnit: "Item_Unit",
                  itemTotal: "Item_Total",
                  empName: "Emp_Name",
                  deptName: "Dept_Name",
                  remark: "WTDoc_Remark",
                }}
                onExportReady={handleExportReady}
                showExportButton={false}
              />

              <div className="col-md-12 mt-4">
                <div className="card">
                  <div className="card-header">
                    <h4>สรุปยอดรวมตามแผนก</h4>
                  </div>
                  <div className="card-body">
                    <div
                      className="table-responsive"
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                      <table className="table table-striped table-hover">
                        <thead className="thead-dark">
                          <tr>
                            <th className="text-center">แผนก</th>
                            <th className="text-center">ยอดรวม (บาท)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summaryData.length > 0 ? (
                            summaryData.map((data, index) => (
                              <tr key={index}>
                                <td className="text-left">
                                  {data.Dept_Name || "-"}
                                </td>
                                <td className="text-right">
                                  {data.Total_Amount
                                    ? Number(data.Total_Amount).toLocaleString(
                                      "th-TH",
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )
                                    : "0.00"}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="2" className="text-center">
                                <h5>ไม่พบข้อมูล</h5>
                              </td>
                            </tr>
                          )}
                          {summaryData.length > 0 && (
                            <tr className="table-info font-weight-bold">
                              <td className="text-center">รวมทั้งหมด</td>
                              <td className="text-right">
                                {summaryData
                                  .reduce(
                                    (total, item) =>
                                      total + (Number(item.Total_Amount) || 0),
                                    0
                                  )
                                  .toLocaleString("th-TH", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InternalMaterialRequisitionReport;
